import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import useAgeOptions from 'hooks/Search/useAgeOptions'
import { useCallback, useMemo } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import { AgeSelect } from './OccupancyChildAgeSelect'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import styled from 'styled-components'

const AgeTooltip = styled(Tooltip)`
  display: flex;
  align-items: center;
`

const AgeTooltipButton = styled(Clickable)`
  display: flex;
  align-items: center;
`

interface Props {
  adultId: number
  age?: number
  minAge: number
  name: string
  onChange: (adultId: number, age: number) => void
}

function OccupancyAdultAgeSelect({ adultId, age, minAge, name, onChange }: Props) {
  const intl = useIntl()
  const ageOptions = useAgeOptions(150)
  const options = useMemo(() => ageOptions.slice(minAge), [ageOptions, minAge])
  const label = intl.formatMessage(
    {
      id: 'xgLL_5PRgk8TYoJwf9zL',
      defaultMessage: 'Adult {adultNumber}',
    },
    { adultNumber: adultId + 1 },
  )
  const ageTooltip = intl.formatMessage({
    id: 'qeauZAB37uwNrZ_8C5qu',
    defaultMessage: 'Age at the end of the tour',
  })
  const requiredErrorMessage = intl.formatMessage({
    id: 'haQhZ0ImE-MPwb3P1wPi',
    defaultMessage: 'Please select the adult traveller’s age',
  })
  const selectPlaceholder = intl.formatMessage({
    id: 'wGdrR8Og2EyHnwyz7BOm',
    defaultMessage: 'Select',
  })

  const onSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    onChange(adultId, Number(event.currentTarget.value))
  }, [adultId, onChange])

  return <Group
    direction="horizontal"
    tabletDirection="vertical"
    horizontalAlign="space-between"
    gap={12}
    verticalAlign="space-between"
    style={{ position: 'relative' }}
  >
    <Group direction="horizontal" gap={4} verticalAlign="center">
      <BodyText variant="medium">{label}</BodyText>
      <AgeTooltip placement="top" description={ageTooltip}>
        <AgeTooltipButton aria-label={ageTooltip}>
          <LineInfoCircleIcon size="XS" colour="light-foreground-alt" />
        </AgeTooltipButton>
      </AgeTooltip>
    </Group>
    <AgeSelect
      data-testid={`occupancy-adult-age-${adultId}`}
      aria-label={label}
      name={`${name}[${adultId}]::number`}
      value={age !== undefined && age >= minAge ? age : ''}
      onChange={onSelectChange}
      noValidationMessage
      noValidationSpacing
      requiredErrorMessage={requiredErrorMessage}
      placeholder={selectPlaceholder}
      required
    >
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </AgeSelect>
  </Group>
}

export default OccupancyAdultAgeSelect
